import { useCallback } from 'react';
import { container } from '@src/container';
import type { AnalyticsService } from '@src/application/analytics/usecases/analytics.service';

/**
 * Hook to access analytics tracking functionality
 * Provides methods to track various events and actions
 */
export function useAnalytics() {
    const analyticsService = container.resolve<AnalyticsService>('AnalyticsService');

    const trackEvent = useCallback(
        (params: {
            category: string;
            action: string;
            label?: string;
            value?: number;
            metadata?: Record<string, any>;
        }) => {
            analyticsService.trackEvent(params);
        },
        [analyticsService]
    );

    const trackPageView = useCallback(
        (params: {
            path: string;
            title: string;
            location?: string;
            metadata?: Record<string, any>;
        }) => {
            analyticsService.trackPageView(params);
        },
        [analyticsService]
    );

    const trackPascoView = useCallback(
        (pascoId: string, pascoTitle: string, metadata?: Record<string, any>) => {
            analyticsService.trackPascoView(pascoId, pascoTitle, metadata);
        },
        [analyticsService]
    );

    const trackPascoClick = useCallback(
        (pascoId: string, clickType: string, metadata?: Record<string, any>) => {
            analyticsService.trackPascoClick(pascoId, clickType, metadata);
        },
        [analyticsService]
    );

    const trackPepperBotUsage = useCallback(
        (action: string, metadata?: Record<string, any>) => {
            analyticsService.trackPepperBotUsage(action, metadata);
        },
        [analyticsService]
    );

    const trackSearch = useCallback(
        (query: string, resultCount: number, metadata?: Record<string, any>) => {
            analyticsService.trackSearch(query, resultCount, metadata);
        },
        [analyticsService]
    );

    const trackDownload = useCallback(
        (fileName: string, fileType: string, metadata?: Record<string, any>) => {
            analyticsService.trackDownload(fileName, fileType, metadata);
        },
        [analyticsService]
    );

    const trackButtonClick = useCallback(
        (buttonName: string, location: string, metadata?: Record<string, any>) => {
            analyticsService.trackButtonClick(buttonName, location, metadata);
        },
        [analyticsService]
    );

    const trackFormSubmission = useCallback(
        (formName: string, success: boolean, metadata?: Record<string, any>) => {
            analyticsService.trackFormSubmission(formName, success, metadata);
        },
        [analyticsService]
    );

    const trackError = useCallback(
        (errorMessage: string, errorType: string, metadata?: Record<string, any>) => {
            analyticsService.trackError(errorMessage, errorType, metadata);
        },
        [analyticsService]
    );

    const setUserProperties = useCallback(
        (params: {
            userId?: string;
            userRole?: string;
            universityId?: string;
            universityName?: string;
            properties?: Record<string, any>;
        }) => {
            analyticsService.setUserProperties(params);
        },
        [analyticsService]
    );

    const clearUserData = useCallback(() => {
        analyticsService.clearUserData();
    }, [analyticsService]);

    return {
        trackEvent,
        trackPageView,
        trackPascoView,
        trackPascoClick,
        trackPepperBotUsage,
        trackSearch,
        trackDownload,
        trackButtonClick,
        trackFormSubmission,
        trackError,
        setUserProperties,
        clearUserData,
    };
}
